import { defineMessages } from '../define';

/**
 * Bank alerts read into transactions, and the rules that read them.
 *
 * Three vocabularies deliberately stay out of this file because they are not
 * prose: the regex itself, the named capture groups the parser recognises
 * (`amount`, `balance`, …), and the wire values `CREDIT` / `DEBIT`. A currency
 * code, an account number and the `bank` field are the operator's own text and
 * are shown exactly as they were typed or captured.
 */
export const banking = defineMessages({
  en: {
    'banking.feed.configure': 'Send to an endpoint',
    'banking.feed.configured': 'Endpoint',
    'banking.feed.title': 'Send transactions to an endpoint',
    'banking.feed.description':
      'Transactions that name the other side are posted the moment they are recorded, and again if a later read of the same alert changes one.',
    'banking.feed.url': 'Endpoint URL',
    'banking.feed.urlHint': 'Must answer 2xx. Failed attempts are retried and kept in the delivery history.',
    'banking.feed.active': 'Sending enabled',
    'banking.feed.defaultName': 'Bank transactions',
    'banking.feed.saved': 'Endpoint saved',
    'banking.feed.removed': 'Endpoint removed',
    'banking.feed.removeTitle': 'Stop sending transactions?',
    'banking.feed.removeDescription':
      'The endpoint and its delivery history go. Recorded transactions are untouched.',
    'banking.feed.secretTitle': 'Signing secret',
    'banking.feed.secretHint': 'Shown once. Use it to verify the X-Threadline-Signature header.',
    'banking.feed.payloadHint':
      'The body carries the transaction exactly as stored: direction, amount, currency, account, their account, counterparty, balance and the time the bank gave. A payment that names nobody - a bill or a fee - is not sent.',
    'banking.column.counterpartyAccount': 'Their account',
    'banking.column.counterparty': 'From / to',
    'banking.nav.transactions': 'Transactions',
    'banking.nav.rules': 'Bank rules',

    'banking.requestFailed':
      'The request could not be completed. Check that the gateway is reachable.',

    'banking.transactions.title': 'Bank transactions',
    'banking.transactions.description':
      'Amounts read out of the alerts your banks send to LINE. Nothing here is typed in by hand — every row came from a message a rule matched.',

    'banking.metric.credit': 'Money in',
    'banking.metric.debit': 'Money out',
    'banking.metric.net': 'Net',
    'banking.metric.count': 'Transactions',
    'banking.metric.hint': 'Across the current filter',

    'banking.filter.direction': 'Filter by direction',
    'banking.filter.account': 'Filter by account',
    'banking.filter.from': 'From date',
    'banking.filter.to': 'To date',
    'banking.direction.all': 'In and out',
    'banking.direction.CREDIT': 'Money in',
    'banking.direction.DEBIT': 'Money out',
    'banking.account.all': 'All accounts',

    'banking.column.occurredAt': 'Occurred',
    'banking.column.direction': 'Direction',
    'banking.column.amount': 'Amount',
    'banking.column.account': 'Account',
    'banking.column.balanceAfter': 'Balance after',
    'banking.column.bank': 'Bank',
    'banking.column.session': 'Session',
    'banking.filter.session': 'Filter by session',
    'banking.session.all': 'All sessions',
    'banking.column.chat': 'Chat',

    'banking.unit.transactions': 'transactions',

    'banking.transactions.empty.title': 'No transactions yet',
    'banking.transactions.empty.description':
      'A row appears here as soon as one of your rules matches a message from a bank. Write the rule for that bank first, then re-read the messages already stored.',
    'banking.transactions.empty.action': 'Go to bank rules',
    'banking.transactions.empty.filtered.title': 'Nothing matches these filters',
    'banking.transactions.empty.filtered.description':
      'Widen the date range or clear the filters to see the rest.',

    'banking.rules.title': 'Bank rules',
    'banking.rules.description':
      'One pattern per alert format. The first enabled rule that matches a message wins, so a specific format belongs above a catch-all.',
    'banking.rules.add': 'New rule',
    'banking.rules.reprocess': 'Reprocess',
    'banking.rules.reprocess.hint':
      'Re-reads the messages already stored with the current rules. This is what picks up the backlog a bank sent before the rule existed.',
    'banking.rules.reprocess.done': 'Re-read {scanned} messages, {matched} produced a transaction',

    'banking.column.name': 'Rule',
    'banking.column.enabled': 'On',
    'banking.column.chatPattern': 'Chat filter',
    'banking.column.priority': 'Priority',
    'banking.column.matchCount': 'Matches',
    'banking.column.lastMatch': 'Last match',

    'banking.rule.enable': 'Enable {name}',
    'banking.rule.disable': 'Disable {name}',
    'banking.rule.deleteLabel': 'Delete {name}',
    'banking.rule.everyChat': 'Every chat',

    'banking.delete.title': 'Delete {name}?',
    'banking.delete.description':
      'The transactions it already produced are kept — they only lose the link back to the rule.',
    'banking.delete.confirm': 'Delete rule',

    'banking.rules.empty.title': 'No bank rules yet',
    'banking.rules.empty.description':
      "A rule tells Threadline how to read one bank's alert: which chat it arrives in, and a pattern that captures the amount, the account and whether money went in or out.",

    'banking.transfer.export': 'Export',
    'banking.transfer.export.hint':
      'Saves every rule below to a file: the patterns and their settings, not how often each has matched here.',
    'banking.transfer.export.done': 'Exported {count} rules',
    'banking.transfer.export.empty': 'There are no rules to export',

    'banking.transfer.import': 'Import',
    'banking.transfer.import.title': 'Import rules',
    'banking.transfer.import.description':
      'Rules are matched by name and bank. One with a name already here is updated; the rest are added.',
    'banking.transfer.import.pick': 'Choose a file',
    'banking.transfer.import.picked': '{name} — {count} rules',
    'banking.transfer.import.unreadable': 'That file is not a rules export',
    'banking.transfer.import.mode': 'Rules not in the file',
    'banking.transfer.import.mode.merge': 'Leave them alone',
    'banking.transfer.import.mode.replace': 'Delete them',
    'banking.transfer.import.replaceWarning':
      'Deleting a rule keeps the transactions it produced; they only lose the link back to it.',
    'banking.transfer.import.preview': 'What will change',
    'banking.transfer.import.previewing': 'Checking the file…',
    'banking.transfer.import.plan': '{created} added, {updated} updated, {removed} deleted',
    'banking.transfer.import.nothing': 'Nothing would change',
    'banking.transfer.import.confirm': 'Apply',
    'banking.transfer.import.done': '{created} added, {updated} updated, {removed} deleted',
    'banking.transfer.action.create': 'Add',
    'banking.transfer.action.update': 'Update',
    'banking.transfer.action.remove': 'Delete',

    'banking.toast.created': 'Rule created',
    'banking.toast.updated': 'Rule saved',
    'banking.toast.deleted': 'Rule deleted',

    'banking.dialog.createTitle': 'New bank rule',
    'banking.dialog.editTitle': 'Edit bank rule',
    'banking.dialog.description':
      'Paste a real alert into the tester and watch what the pattern pulls out of it before you save. A bank that reworded its messages is the usual reason to be here.',

    'banking.field.name': 'Name',
    'banking.field.bank': 'Bank',
    'banking.field.chatPattern': 'Chat filter',
    'banking.field.priority': 'Priority',
    'banking.field.currency': 'Currency',
    'banking.field.yearOffset': 'Year offset',
    'banking.field.defaultDirection': 'Default direction',
    'banking.field.pattern': 'Pattern',
    'banking.field.flags': 'Flags',

    'banking.hint.bank': 'Groups the transactions this rule produces, and is shown as you type it.',
    'banking.hint.chatPattern':
      'Part of the chat name, matched without case. Leave it empty to read every chat.',
    'banking.hint.priority': 'Lowest first, and the first match wins.',
    'banking.hint.currency': 'Used when the pattern captures no currency of its own.',
    'banking.hint.yearOffset':
      'Subtracted from the captured year. 543 turns a Buddhist year into a Gregorian one.',
    'banking.hint.defaultDirection':
      'Used when the pattern captures nothing the direction map recognises.',
    'banking.hint.pattern':
      'A JavaScript regular expression with named groups. The amount is the only group it must capture; anything missing falls back to the fields above.',
    'banking.hint.flags': 'For example i to ignore case. g is dropped.',

    'banking.defaultDirection.none': 'None — the message must say',

    'banking.groups.title': 'Groups the parser reads',
    'banking.groups.hint':
      'Anything else you capture still shows up in the tester, but is not stored.',

    'banking.directionMap.title': 'Direction map',
    'banking.directionMap.hint':
      'Turns the words the direction group captured into money in or money out. Compared without case, so one entry per wording the bank uses.',
    'banking.directionMap.add': 'Add wording',
    'banking.directionMap.empty': 'No wordings mapped yet.',
    'banking.directionMap.keyLabel': 'Captured wording {index}',
    'banking.directionMap.valueLabel': 'Direction for wording {index}',
    'banking.directionMap.remove': 'Remove wording {index}',

    'banking.tester.title': 'Live tester',
    'banking.tester.description':
      'Runs the same parser the ingest path runs, and stores nothing.',
    'banking.tester.sampleLabel': 'Sample message',
    'banking.tester.samplePlaceholder': 'Paste an alert exactly as the bank sent it…',
    'banking.tester.needsPattern': 'Write a pattern to start testing.',
    'banking.tester.idle': 'Paste an alert above to see what this rule reads out of it.',
    'banking.tester.checking': 'Reading…',
    'banking.tester.matched': 'Parsed',
    'banking.tester.noMatch': 'Not parsed',
    'banking.tester.groups': 'Captured groups',
  },
  th: {
    'banking.feed.configure': 'ส่งไปยัง endpoint',
    'banking.feed.configured': 'endpoint',
    'banking.feed.title': 'ส่งรายการเดินบัญชีไปยัง endpoint',
    'banking.feed.description':
      'ระบบจะส่งเฉพาะรายการที่ระบุคู่โอน ทันทีที่บันทึก และส่งอีกครั้งถ้าการอ่านข้อความเดิมรอบหลังทำให้ข้อมูลเปลี่ยน',
    'banking.feed.url': 'URL ของ endpoint',
    'banking.feed.urlHint': 'ต้องตอบ 2xx หากส่งไม่สำเร็จระบบจะลองใหม่และเก็บไว้ในประวัติการส่ง',
    'banking.feed.active': 'เปิดการส่ง',
    'banking.feed.defaultName': 'รายการเดินบัญชี',
    'banking.feed.saved': 'บันทึก endpoint แล้ว',
    'banking.feed.removed': 'ลบ endpoint แล้ว',
    'banking.feed.removeTitle': 'หยุดส่งรายการเดินบัญชีหรือไม่',
    'banking.feed.removeDescription':
      'endpoint และประวัติการส่งจะถูกลบ ส่วนรายการเดินบัญชีที่บันทึกไว้ยังอยู่ครบ',
    'banking.feed.secretTitle': 'กุญแจสำหรับตรวจลายเซ็น',
    'banking.feed.secretHint': 'แสดงครั้งเดียว ใช้ตรวจสอบ header X-Threadline-Signature',
    'banking.feed.payloadHint':
      'เนื้อหาที่ส่งคือรายการตามที่บันทึกไว้ทุกช่อง: ทิศทาง ยอดเงิน สกุลเงิน บัญชี เลขบัญชีคู่โอน ชื่อคู่โอน ยอดคงเหลือ และเวลาที่ธนาคารระบุ ส่วนรายการที่ไม่ระบุคู่โอน เช่น จ่ายบิลหรือค่าธรรมเนียม จะไม่ถูกส่ง',
    'banking.column.counterpartyAccount': 'เลขบัญชีคู่โอน',
    'banking.column.counterparty': 'โอนจาก / โอนไป',
    'banking.nav.transactions': 'รายการเดินบัญชี',
    'banking.nav.rules': 'กฎธนาคาร',

    'banking.requestFailed': 'ดำเนินการตามคำขอไม่สำเร็จ กรุณาตรวจสอบว่าเชื่อมต่อเกตเวย์ได้',

    'banking.transactions.title': 'รายการเดินบัญชี',
    'banking.transactions.description':
      'ยอดเงินที่ระบบอ่านได้จากข้อความแจ้งเตือนของธนาคารที่ส่งเข้ามาใน LINE ทุกแถวมาจากข้อความที่กฎจับคู่ได้ ไม่มีรายการใดที่กรอกด้วยมือ',

    'banking.metric.credit': 'เงินเข้า',
    'banking.metric.debit': 'เงินออก',
    'banking.metric.net': 'สุทธิ',
    'banking.metric.count': 'จำนวนรายการ',
    'banking.metric.hint': 'ตามตัวกรองที่ใช้อยู่',

    'banking.filter.direction': 'กรองตามประเภทรายการ',
    'banking.filter.account': 'กรองตามบัญชี',
    'banking.filter.from': 'ตั้งแต่วันที่',
    'banking.filter.to': 'ถึงวันที่',
    'banking.direction.all': 'ทั้งเงินเข้าและเงินออก',
    'banking.direction.CREDIT': 'เงินเข้า',
    'banking.direction.DEBIT': 'เงินออก',
    'banking.account.all': 'ทุกบัญชี',

    'banking.column.occurredAt': 'เวลาที่เกิดรายการ',
    'banking.column.direction': 'ประเภท',
    'banking.column.amount': 'จำนวนเงิน',
    'banking.column.account': 'บัญชี',
    'banking.column.balanceAfter': 'ยอดคงเหลือ',
    'banking.column.bank': 'ธนาคาร',
    'banking.column.session': 'เซสชัน',
    'banking.filter.session': 'กรองตามเซสชัน',
    'banking.session.all': 'ทุกเซสชัน',
    'banking.column.chat': 'แชท',

    'banking.unit.transactions': 'รายการ',

    'banking.transactions.empty.title': 'ยังไม่มีรายการเดินบัญชี',
    'banking.transactions.empty.description':
      'รายการจะขึ้นที่นี่ทันทีที่มีกฎข้อใดข้อหนึ่งจับคู่กับข้อความจากธนาคารได้ ให้เขียนกฎของธนาคารนั้นก่อน แล้วค่อยสั่งอ่านข้อความที่เก็บไว้แล้วอีกครั้ง',
    'banking.transactions.empty.action': 'ไปที่กฎธนาคาร',
    'banking.transactions.empty.filtered.title': 'ไม่มีรายการที่ตรงกับตัวกรอง',
    'banking.transactions.empty.filtered.description':
      'ลองขยายช่วงวันที่ หรือล้างตัวกรองเพื่อดูรายการที่เหลือ',

    'banking.rules.title': 'กฎการอ่านข้อความธนาคาร',
    'banking.rules.description':
      'หนึ่งกฎต่อรูปแบบข้อความแจ้งเตือนหนึ่งแบบ ระบบจะใช้กฎที่เปิดอยู่และจับคู่ได้เป็นข้อแรก จึงควรวางรูปแบบที่เจาะจงไว้เหนือกฎกว้าง ๆ',
    'banking.rules.add': 'สร้างกฎใหม่',
    'banking.rules.reprocess': 'อ่านย้อนหลัง',
    'banking.rules.reprocess.hint':
      'อ่านข้อความที่เก็บไว้แล้วอีกครั้งด้วยกฎชุดปัจจุบัน ใช้เมื่อเพิ่งเขียนหรือเพิ่งแก้กฎ ข้อความเก่าที่ธนาคารส่งมาก่อนจะมีกฎจึงจะถูกอ่าน',
    'banking.rules.reprocess.done': 'อ่านซ้ำ {scanned} ข้อความ ได้รายการเดินบัญชี {matched} รายการ',

    'banking.column.name': 'ชื่อกฎ',
    'banking.column.enabled': 'เปิดใช้',
    'banking.column.chatPattern': 'ตัวกรองชื่อแชท',
    'banking.column.priority': 'ลำดับ',
    'banking.column.matchCount': 'จับคู่แล้ว',
    'banking.column.lastMatch': 'จับคู่ล่าสุด',

    'banking.rule.enable': 'เปิดใช้งาน {name}',
    'banking.rule.disable': 'ปิดใช้งาน {name}',
    'banking.rule.deleteLabel': 'ลบ {name}',
    'banking.rule.everyChat': 'ทุกแชท',

    'banking.delete.title': 'ลบ {name} หรือไม่',
    'banking.delete.description':
      'รายการเดินบัญชีที่กฎนี้เคยสร้างไว้จะยังอยู่ครบ เพียงแต่จะไม่เชื่อมกลับมาที่กฎนี้อีก',
    'banking.delete.confirm': 'ลบกฎ',

    'banking.rules.empty.title': 'ยังไม่มีกฎธนาคาร',
    'banking.rules.empty.description':
      'กฎคือวิธีบอกให้ Threadline รู้ว่าจะอ่านข้อความแจ้งเตือนของธนาคารหนึ่ง ๆ อย่างไร ทั้งแชทที่ข้อความเข้ามา และรูปแบบที่ใช้ดึงจำนวนเงิน เลขบัญชี และประเภทว่าเงินเข้าหรือเงินออก',

    'banking.transfer.export': 'ส่งออก',
    'banking.transfer.export.hint':
      'บันทึกกฎทั้งหมดข้างล่างลงไฟล์ ได้ทั้งรูปแบบและการตั้งค่า แต่ไม่รวมจำนวนครั้งที่กฎนั้นเคยจับได้บนเครื่องนี้',
    'banking.transfer.export.done': 'ส่งออก {count} กฎแล้ว',
    'banking.transfer.export.empty': 'ยังไม่มีกฎให้ส่งออก',

    'banking.transfer.import': 'นำเข้า',
    'banking.transfer.import.title': 'นำเข้ากฎ',
    'banking.transfer.import.description':
      'จับคู่กฎด้วยชื่อและธนาคาร ถ้าชื่อซ้ำกับที่มีอยู่จะเป็นการแก้ไขทับ ที่เหลือจะเพิ่มเข้ามาใหม่',
    'banking.transfer.import.pick': 'เลือกไฟล์',
    'banking.transfer.import.picked': '{name} — {count} กฎ',
    'banking.transfer.import.unreadable': 'ไฟล์นี้ไม่ใช่ไฟล์ส่งออกกฎธนาคาร',
    'banking.transfer.import.mode': 'กฎที่ไม่มีในไฟล์',
    'banking.transfer.import.mode.merge': 'เก็บไว้ตามเดิม',
    'banking.transfer.import.mode.replace': 'ลบทิ้ง',
    'banking.transfer.import.replaceWarning':
      'การลบกฎไม่กระทบรายการเดินบัญชีที่กฎนั้นเคยสร้างไว้ เพียงแต่รายการจะไม่เชื่อมกลับมาที่กฎอีก',
    'banking.transfer.import.preview': 'สิ่งที่จะเปลี่ยน',
    'banking.transfer.import.previewing': 'กำลังตรวจไฟล์…',
    'banking.transfer.import.plan': 'เพิ่ม {created} แก้ไข {updated} ลบ {removed}',
    'banking.transfer.import.nothing': 'ไม่มีอะไรเปลี่ยน',
    'banking.transfer.import.confirm': 'นำเข้า',
    'banking.transfer.import.done': 'เพิ่ม {created} แก้ไข {updated} ลบ {removed}',
    'banking.transfer.action.create': 'เพิ่ม',
    'banking.transfer.action.update': 'แก้ไข',
    'banking.transfer.action.remove': 'ลบ',

    'banking.toast.created': 'สร้างกฎแล้ว',
    'banking.toast.updated': 'บันทึกกฎแล้ว',
    'banking.toast.deleted': 'ลบกฎแล้ว',

    'banking.dialog.createTitle': 'กฎธนาคารใหม่',
    'banking.dialog.editTitle': 'แก้ไขกฎธนาคาร',
    'banking.dialog.description':
      'วางข้อความแจ้งเตือนจริงลงในช่องทดสอบ แล้วดูว่ารูปแบบที่เขียนไว้ดึงค่าอะไรออกมาได้บ้างก่อนกดบันทึก ส่วนใหญ่ที่ต้องมาแก้ตรงนี้คือธนาคารเปลี่ยนคำในข้อความ',

    'banking.field.name': 'ชื่อกฎ',
    'banking.field.bank': 'ธนาคาร',
    'banking.field.chatPattern': 'ตัวกรองชื่อแชท',
    'banking.field.priority': 'ลำดับความสำคัญ',
    'banking.field.currency': 'สกุลเงิน',
    'banking.field.yearOffset': 'ส่วนต่างปี',
    'banking.field.defaultDirection': 'ประเภทรายการเริ่มต้น',
    'banking.field.pattern': 'รูปแบบ',
    'banking.field.flags': 'แฟล็ก',

    'banking.hint.bank': 'ใช้จัดกลุ่มรายการที่กฎนี้สร้างขึ้น และแสดงตามที่พิมพ์ไว้',
    'banking.hint.chatPattern':
      'ใส่บางส่วนของชื่อแชท ไม่สนตัวพิมพ์เล็กใหญ่ เว้นว่างไว้หากต้องการอ่านทุกแชท',
    'banking.hint.priority': 'ค่าน้อยมาก่อน และกฎแรกที่จับคู่ได้เป็นผู้ชนะ',
    'banking.hint.currency': 'ใช้เมื่อรูปแบบไม่ได้จับสกุลเงินมาเอง',
    'banking.hint.yearOffset': 'จะถูกลบออกจากปีที่จับได้ ใส่ 543 เพื่อแปลงปี พ.ศ. เป็น ค.ศ.',
    'banking.hint.defaultDirection': 'ใช้เมื่อไม่มีคำใดที่ตารางแปลงรู้จักถูกจับมาได้',
    'banking.hint.pattern':
      'regex ของ JavaScript พร้อมกลุ่มแบบตั้งชื่อ มีเพียงจำนวนเงินเท่านั้นที่จำเป็นต้องจับให้ได้ ส่วนที่ขาดไปจะใช้ค่าจากช่องด้านบนแทน',
    'banking.hint.flags': 'เช่น i เพื่อไม่สนตัวพิมพ์เล็กใหญ่ ส่วน g จะถูกตัดทิ้ง',

    'banking.defaultDirection.none': 'ไม่กำหนด — ต้องอ่านจากข้อความเท่านั้น',

    'banking.groups.title': 'กลุ่มที่ตัวอ่านรู้จัก',
    'banking.groups.hint': 'กลุ่มอื่นที่จับได้จะยังแสดงในช่องทดสอบ แต่ระบบจะไม่เก็บไว้',

    'banking.directionMap.title': 'ตารางแปลงประเภทรายการ',
    'banking.directionMap.hint':
      'แปลงคำที่กลุ่ม direction จับได้ให้เป็นเงินเข้าหรือเงินออก เทียบโดยไม่สนตัวพิมพ์เล็กใหญ่ ให้ใส่หนึ่งบรรทัดต่อหนึ่งคำที่ธนาคารใช้',
    'banking.directionMap.add': 'เพิ่มคำ',
    'banking.directionMap.empty': 'ยังไม่ได้กำหนดคำใดไว้',
    'banking.directionMap.keyLabel': 'คำที่จับได้ที่ {index}',
    'banking.directionMap.valueLabel': 'ประเภทรายการของคำที่ {index}',
    'banking.directionMap.remove': 'ลบคำที่ {index}',

    'banking.tester.title': 'ทดลองอ่านข้อความ',
    'banking.tester.description': 'ใช้ตัวอ่านชุดเดียวกับตอนรับข้อความจริง และไม่บันทึกอะไรทั้งสิ้น',
    'banking.tester.sampleLabel': 'ข้อความตัวอย่าง',
    'banking.tester.samplePlaceholder': 'วางข้อความแจ้งเตือนตามที่ธนาคารส่งมาจริง…',
    'banking.tester.needsPattern': 'กรอกรูปแบบก่อนจึงจะทดสอบได้',
    'banking.tester.idle': 'วางข้อความแจ้งเตือนด้านบน เพื่อดูว่ากฎนี้อ่านค่าอะไรออกมาได้บ้าง',
    'banking.tester.checking': 'กำลังอ่าน…',
    'banking.tester.matched': 'อ่านค่าได้',
    'banking.tester.noMatch': 'อ่านค่าไม่ได้',
    'banking.tester.groups': 'กลุ่มที่จับได้',
  },
});
